import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import Booking from '../../../Domain/Bookings/Booking';
import CancelBookingUseCase from '../../../Domain/Bookings/CancelBookingUseCase';
import GetCustomerFutureBookingsUseCase from '../../../Domain/Bookings/GetCustomerFutureBookingsUseCase';
import GetTokensForCurrentWeekUseCase from '../../../Domain/Bookings/GetTokensForCurrentWeekUseCase';
import GetCustomerUseCase from '../../../Domain/Customers/GetCustomerUseCase';
import Customer from '../../../Domain/Entities/Customer';
import { getPackageState, selectCurrentPackage, selectDisplayablePackages, selectUpcomingPackage } from '../../../Domain/Entities/Package';
import GetUserUseCase from '../../../Domain/UserAuth/GetUserUseCase';
import Logger from '../../../Logger/Logger';
import { Constants } from '../../../Utils/Constants';
import DateTimeProvider from '../../../Utils/DateTimeProvider';
import { format } from '../../../Utils/DateUtils';
import UiBasicAppointment from '../../Admin/Customers/Detail/UiBasicAppointment';
import { mapPackageToUiSimplePackage } from '../../Admin/Customers/Detail/UiSimplePackage';
import UiUserOverview from './UiUserOverview';
import UserOverviewView from './UserOverviewView';
import { perUsagePackage } from "../../../Domain/Entities/PackageTypes";
import strings from '../../Utils/LocalizedStrings';

export default class UserOverviewPresenter {

  private readonly subscription = new Subscription();
  private readonly customer: Observable<Customer>;
  private readonly bookings: Observable<Booking[]>;
  private readonly usedWeekTokensCount: Observable<number>;

  private view?: UserOverviewView;

  constructor(
    getUserUseCase: GetUserUseCase,
    getCustomerUseCase: GetCustomerUseCase,
    getCustomerWeekBookingsUseCase: GetCustomerFutureBookingsUseCase,
    getTokensForCurrentWeekUseCase: GetTokensForCurrentWeekUseCase,
    private readonly cancelBookingUseCaseProvider: () => CancelBookingUseCase,
    private readonly dateTimeProvider: DateTimeProvider,
  ) {
    // The current user is a customer of the system.
    const user = getUserUseCase.execute().pipe(shareReplay(1));

    this.customer = user.pipe(
      switchMap(user => getCustomerUseCase.execute(user.identifier)),
      shareReplay(1)
    );
    this.bookings = user.pipe(
      switchMap(() => getCustomerWeekBookingsUseCase.execute()),
      shareReplay(1)
    );
    this.usedWeekTokensCount = getTokensForCurrentWeekUseCase.execute().pipe(shareReplay(1));
  }

  attachView = (v: UserOverviewView) => {
    this.view = v;

    this.subscription.add(
      combineLatest(this.customer, this.usedWeekTokensCount)
        .pipe(map(([customer, tokensUsedInWeek]) =>
          this.toUiUserOverview(
            new Date(this.dateTimeProvider.currentTimeMillis()),
            customer, tokensUsedInWeek
          )
        ))
        .subscribe(
          overview => this.view?.showUserOverview(overview),
          error => {
            this.view?.showError(strings.user.overview.errorLoadingData);
            Logger.e(error);
          }
        )
    );

    this.subscription.add(
      this.bookings
        .pipe(map(bookings => bookings.map(this.toAppointment)))
        .subscribe(
          displayableBookings => this.view?.showBookings(displayableBookings),
          error => {
            this.view?.showError(strings.user.overview.errorLoadingBookings);
            Logger.e(error);
          }
        )
    );
  }

  detachView = () => {
    this.view = undefined;
    this.subscription.unsubscribe();
  }

  cancelBooking = (identifier: string) => {
    this.subscription.add(
      this.bookings.pipe(take(1))
        .subscribe(
          bookings => {
            const booking = bookings.find(booking => booking.identifier === identifier)
            if (booking) {
              const day = format(booking.startDate, "EEEE dd MMMM yyyy");
              const time = format(booking.startDate, "HH:mm");
              this.view?.showBookingCancellationDialog(
                booking.identifier,
                strings.formatString(strings.user.overview.cancelAppointmentConfirm, day, time) as string
              );
            }
          },
          Logger.e
        )
    )
  }

  confirmBookingCancellation = (identifier: string | undefined | null) => {
    if (!identifier) return;
    this.subscription.add(
      this.cancelBookingUseCaseProvider()
        .execute(identifier)
        .subscribe({
          error: Logger.e
        })
    );
  }

  // Private methods

  private toUiUserOverview = (currentDate: Date, customer: Customer, weekBookingsCount: number): UiUserOverview => {
    const withState = customer.packages.map(pack => ({
      ...pack,
      state: getPackageState(pack, currentDate)
    }));

    // The package driving the token counter is the one valid today, regardless of what the list
    // below shows: an old expired package must never be picked up as the current one. With nothing
    // valid today, fall back to the package starting next — the customer already bought it and can
    // already book for the days it covers, so its tokens are the ones that matter.
    const activePackage = selectCurrentPackage(withState, currentDate);
    const upcomingPackage = activePackage ? null : selectUpcomingPackage(withState, currentDate);
    const counterPackage = activePackage ?? upcomingPackage;
    const packages = selectDisplayablePackages(withState, currentDate);
    const totalAvailableTokensInPeriod = counterPackage?.type === perUsagePackage
      ? counterPackage?.initialTokenCount ?? Constants.defaultWeeklyBookingsLimit
      : counterPackage?.maxWeeklyBookingsCount ?? Constants.defaultWeeklyBookingsLimit;
    // Bookings made this week were paid by today's package, never by one that has not started.
    const bookingsSpentOnCounterPackage = upcomingPackage ? 0 : weekBookingsCount;
    const totalLeftTokensInPeriod = counterPackage?.type === perUsagePackage
      ? counterPackage?.leftTokenCount
      : totalAvailableTokensInPeriod - bookingsSpentOnCounterPackage;
    const leftTokenCount = counterPackage?.leftTokenCount ?? 0;
    const formatDate = (date: Date) => format(date, "dd/MM/yyyy");
    const displayablePackages = packages.map(pack => mapPackageToUiSimplePackage(pack, pack.state, formatDate));
    return {
      identifier: customer.identifier,
      name: customer.name,
      currentPackage: counterPackage
        ? mapPackageToUiSimplePackage(counterPackage, counterPackage.state, formatDate)
        : null,
      tokensAvailableFrom: upcomingPackage ? formatDate(upcomingPackage.startDate) : null,
      packages: displayablePackages,
      totalTokenCount: totalAvailableTokensInPeriod,
      leftTokenCount: Math.max(0, Math.min(leftTokenCount, totalLeftTokensInPeriod)),
    }
  }

  private toAppointment: (booking: Booking) => UiBasicAppointment = (booking) => ({
    identifier: booking.identifier,
    displayableDate: format(booking.startDate, "EEEE, dd MMMM yyyy"),
    displayableTimeStart: format(booking.startDate, "HH:mm"),
    displayableTimeEnd: format(booking.endDate, "HH:mm"),
    participantsCount: "",
    isCancelable: booking.isCancelable
  })
};

